import {
  FeatureRankingService,
  FeatureItem,
  FeatureRankingSession,
  UserRankingItem,
  CreateFeatureItemRequest,
  UpdateFeatureItemRequest,
} from '@beeboard/common';
import { defineStore } from 'pinia';

type FeatureRankingState = {
  featureItems: FeatureItem[];
  session: FeatureRankingSession | null;
  userRanking: UserRankingItem[];
  hasUnranked: boolean;
  isLoaded: boolean;
  sessionLoaded: boolean;
  sessionDismissedToday: boolean;
  manualPopupOpen: boolean;
};

const DISMISS_KEY = 'feature-ranking-dismissed';

function isDismissedToday(): boolean {
  const dismissed = localStorage.getItem(DISMISS_KEY);
  if (!dismissed) {
    return false;
  }

  const today = new Date().toISOString().slice(0, 10);

  return dismissed === today;
}

function setDismissedToday(): void {
  const today = new Date().toISOString().slice(0, 10);
  localStorage.setItem(DISMISS_KEY, today);
}

export const useFeatureRankingStore = defineStore('featureRankingStore', {
  state: (): FeatureRankingState => ({
    featureItems: [],
    session: null,
    userRanking: [],
    hasUnranked: false,
    isLoaded: false,
    sessionLoaded: false,
    sessionDismissedToday: isDismissedToday(),
    manualPopupOpen: false,
  }),

  getters: {
    shouldShowPopup(state): boolean {
      return (
        state.manualPopupOpen ||
        (!state.sessionDismissedToday &&
          state.sessionLoaded &&
          state.session !== null)
      );
    },
  },

  actions: {
    async loadFeatureItems() {
      const result = await FeatureRankingService.getFeatureItems();
      this.featureItems = result.items;
      this.isLoaded = true;
    },

    async loadSession() {
      try {
        const result = await FeatureRankingService.getRankingSession();
        this.session = result;
      } catch {
        this.session = null;
      }
      this.sessionLoaded = true;
    },

    async loadUserRanking() {
      const result = await FeatureRankingService.getUserRanking();
      this.userRanking = result.items;
      this.hasUnranked = result.has_unranked;
    },

    async submitRanking(featureIds: string[]) {
      const result = await FeatureRankingService.submitRanking({
        feature_ids: featureIds,
      });
      this.userRanking = result.items;
      this.hasUnranked = result.has_unranked;
      this.manualPopupOpen = false;

      // Reload session to get next unranked feature
      await this.loadSession();

      return result;
    },

    async createFeatureItem(data: CreateFeatureItemRequest) {
      const item = await FeatureRankingService.createFeatureItem(data);
      this.featureItems.push(item);

      return item;
    },

    async updateFeatureItem(id: string, data: UpdateFeatureItemRequest) {
      const item = await FeatureRankingService.updateFeatureItem(id, data);
      const index = this.featureItems.findIndex((f) => f.id === id);
      if (index !== -1) {
        this.featureItems[index] = item;
      }

      return item;
    },

    async deleteFeatureItem(id: string) {
      await FeatureRankingService.deleteFeatureItem(id);
      this.featureItems = this.featureItems.filter((f) => f.id !== id);
    },

    dismissPopup() {
      setDismissedToday();
      this.sessionDismissedToday = true;
      this.manualPopupOpen = false;
    },

    async startRankingRound() {
      await this.loadSession();
      if (this.session) {
        this.manualPopupOpen = true;
      }
    },
  },
});
